import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreHorizontal, Plus, Search, Send, UserCheck, UserX } from 'lucide-react'
import { fetchAdminUsers, suspendAdminUser, reactivateAdminUser, resendAdminInvitation } from '../../api'
import { CreateAdminDialog } from '../dialogs/CreateAdminDialog'
import type { AdminUser } from '../../types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuthSession } from '@/features/auth/session'
import { formatAppDateTime } from '@/lib/locale'

function userInitials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

type ConfirmAction = 'suspend' | 'reactivate' | 'resend'

interface ConfirmState {
  user: AdminUser
  action: ConfirmAction
}

function StatusBadge({ status, t }: { status: AdminUser['status']; t: (k: string) => string }) {
  if (status === 'active') {
    return (
      <Badge variant="outline" className="border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400">
        {t('admin.users.status.active')}
      </Badge>
    )
  }
  if (status === 'suspended') {
    return (
      <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
        {t('admin.users.status.suspended')}
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
      {t('admin.users.status.invited')}
    </Badge>
  )
}

export function SuperAdminsTab() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuthSession()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)
  const [suspendReason, setSuspendReason] = useState('')
  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => fetchAdminUsers(),
  })

  const suspendMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      suspendAdminUser(userId, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      toast.success(t('admin.toasts.userSuspended'))
      setConfirm(null)
      setSuspendReason('')
    },
    onError: () => toast.error(t('common.error')),
  })

  const reactivateMutation = useMutation({
    mutationFn: (userId: string) => reactivateAdminUser(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      toast.success(t('admin.toasts.userReactivated'))
      setConfirm(null)
    },
    onError: () => toast.error(t('common.error')),
  })

  const resendMutation = useMutation({
    mutationFn: (userId: string) => resendAdminInvitation(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      toast.success(t('admin.toasts.invitationResent'))
      setConfirm(null)
    },
    onError: () => toast.error(t('common.error')),
  })

  const allUsers = usersQuery.data?.data ?? []
  const filtered = search.trim()
    ? allUsers.filter(
        (u) =>
          u.display_name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()),
      )
    : allUsers

  const isActionPending =
    suspendMutation.isPending || reactivateMutation.isPending || resendMutation.isPending

  function handleCloseConfirm() {
    setConfirm(null)
    setSuspendReason('')
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.users.searchPlaceholder')}
            className="pl-8"
          />
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 size-4" />
          {t('admin.users.createButton')}
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.users.table.user')}</TableHead>
              <TableHead>{t('admin.users.table.status')}</TableHead>
              <TableHead>{t('admin.users.table.roles')}</TableHead>
              <TableHead>{t('admin.users.table.createdAt')}</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersQuery.isPending
              ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="size-8 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-44" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell />
                  </TableRow>
                ))
              : filtered.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-sm text-muted-foreground">
                      {t('admin.users.emptyState')}
                    </TableCell>
                  </TableRow>
                )
                : filtered.map((user) => {
                  const isSelf = user.id === currentUser?.id
                  const showActions = !user.is_foundation && !isSelf

                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="size-8">
                            <AvatarImage src={user.avatar_url ?? undefined} alt={user.display_name} />
                            <AvatarFallback className="text-xs font-semibold">
                              {userInitials(user.display_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium">{user.display_name}</p>
                              {user.is_foundation && (
                                <Badge variant="secondary" className="rounded-full px-1.5 py-0 text-xs">
                                  {t('admin.users.foundationBadge')}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={user.status} t={t} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((r) => (
                            <Badge key={r.id} variant="secondary" className="text-xs">
                              {r.name}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatAppDateTime(user.created_at)}
                      </TableCell>
                      <TableCell>
                        {showActions && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="size-8 p-0">
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {user.status === 'invited' && (
                                <DropdownMenuItem
                                  onClick={() => setConfirm({ user, action: 'resend' })}
                                >
                                  <Send className="mr-2 size-4" />
                                  {t('admin.users.actions.resendInvitation')}
                                </DropdownMenuItem>
                              )}
                              {user.status === 'active' && (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setConfirm({ user, action: 'suspend' })}
                                >
                                  <UserX className="mr-2 size-4" />
                                  {t('admin.users.actions.suspend')}
                                </DropdownMenuItem>
                              )}
                              {user.status === 'suspended' && (
                                <DropdownMenuItem
                                  onClick={() => setConfirm({ user, action: 'reactivate' })}
                                >
                                  <UserCheck className="mr-2 size-4" />
                                  {t('admin.users.actions.reactivate')}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
          </TableBody>
        </Table>
        </div>
      </div>

      <CreateAdminDialog open={createOpen} onOpenChange={setCreateOpen} />

      {confirm && (
        <Dialog open onOpenChange={handleCloseConfirm}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {confirm.action === 'suspend'
                  ? t('admin.users.dialogs.suspend.title')
                  : confirm.action === 'reactivate'
                    ? t('admin.users.dialogs.reactivate.title')
                    : t('admin.users.actions.resendInvitation')}
              </DialogTitle>
              <DialogDescription>
                {confirm.action === 'suspend'
                  ? t('admin.users.dialogs.suspend.description')
                  : confirm.action === 'reactivate'
                    ? t('admin.users.dialogs.reactivate.description')
                    : confirm.user.email}
              </DialogDescription>
            </DialogHeader>

            {confirm.action === 'suspend' && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="suspend-reason">
                  {t('admin.users.dialogs.suspend.reasonLabel')}
                </Label>
                <Textarea
                  id="suspend-reason"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder={t('admin.users.dialogs.suspend.reasonPlaceholder')}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {t('admin.users.dialogs.suspend.reasonHint')}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseConfirm} disabled={isActionPending}>
                {t('admin.users.dialogs.suspend.cancel')}
              </Button>
              <Button
                variant={confirm.action === 'suspend' ? 'destructive' : 'default'}
                disabled={
                  isActionPending ||
                  (confirm.action === 'suspend' && suspendReason.trim().length === 0)
                }
                onClick={() => {
                  if (confirm.action === 'suspend') {
                    suspendMutation.mutate({ userId: confirm.user.id, reason: suspendReason.trim() })
                  } else if (confirm.action === 'reactivate') {
                    reactivateMutation.mutate(confirm.user.id)
                  } else {
                    resendMutation.mutate(confirm.user.id)
                  }
                }}
              >
                {confirm.action === 'suspend'
                  ? t('admin.users.dialogs.suspend.confirm')
                  : confirm.action === 'reactivate'
                    ? t('admin.users.dialogs.reactivate.confirm')
                    : t('admin.users.actions.resendInvitation')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
